import React from 'react';
import { classNames } from '@progress/kendo-react-common';
import { Input } from '@progress/kendo-react-inputs';
import * as _ from 'lodash';
import uuid from 'uuid-random';
import { PureComponent } from '../../../../dispatcher/modules';
import PuzzleButton from '../../button/PuzzleButton';
import {
  PUZZLE_INPUT_WITH_ACTION_ITEM,
  PUZZLE_INPUT_WITH_ACTIONS_PROPS,
  PUZZLE_INPUT_WITH_ACTIONS_STATE
} from './Types';
import Field from '../../../layout/field/Field';
import WithTooltip from '../../../../utility/withTooltip';
import PuzzleTooltip from '../../tooltip/PuzzleTooltip';
import { Label } from '@progress/kendo-react-labels';
import HBox from '../../../layout/container/h_box/HBox';
import {
  BUTTON_SIZE_PROP
} from '../../button/Types';
import { 
  ABSTRACT_FIELD_OMITTED_PROPS, 
  FIELD_OMITTED_PROPS, 
  TOOLTIP_OMITTED_PROPS
} from '../../../../utility';

/**
 * This component is an extended version of Kendo's Input. It supports
 * state management mechanism flagging every state in which component can exist(empty/disabled/invalid),
 * presenting component with search icon on the left side of input or with action button on the right which you can fully customize
 **/
class PuzzleInputWithActions extends PureComponent<PUZZLE_INPUT_WITH_ACTIONS_PROPS, PUZZLE_INPUT_WITH_ACTIONS_STATE> {
  moduleId = uuid();
  inputReference = null;
  tooltip: any = null;
  state:PUZZLE_INPUT_WITH_ACTIONS_STATE = {
    isInputFocused: false
  }

  static defaultProps = {
    tooltipAnchor: 'target',
    tooltipOpenDelay: 1000,
    fieldSize: 'medium'
  };

  /**
   * Action button click handler
   * @param buttonType buttonType from ACTION_BTN_SETTINGS
   * @param e clickEvent
   */
  private actionBtnOnClickHandler = (button: any, e: React.MouseEvent<HTMLButtonElement>) => {
    button?.onClick?.(e);
    this.inputReference?.focus();
  };

  /**
   * Action button render /
   */
  private actionsRender = (actions: PUZZLE_INPUT_WITH_ACTION_ITEM[] = []) => actions.map((action, index: number) => {
    const { fieldSize } = this.props;

    const actionProps = _.omit(
      action,
      ['ariaLabel']
    );
    let btnSize: BUTTON_SIZE_PROP = 'small';
    if (fieldSize === 'large') {
      btnSize = 'medium';
    }
    if (React.isValidElement(action)) {
      return <React.Fragment key={index}>{action}</React.Fragment>;
    } else {
      return (
        <PuzzleButton
          key={index}
          {...actionProps}
          size={btnSize}
          extraSmall={fieldSize === 'small'}
          onClick={e => this.actionBtnOnClickHandler(action, e)}
        />
      );
    }
  })

  /**
   * Sets field valid state
   * @param resourceExists
   */
  setValid = (resourceExists: boolean) => {
    _.get(this, 'props.setValid') && this.props.setValid(resourceExists);
  };

  renderBody() {
    const {
      asField,
      ariaDescribedBy,
      className = '',
      dataCy = 'puzzle-input-wrap',
      disabled,
      label,
      labelClassName = '',
      locked = false,
      errorTxt,
      value,
      valid,
      fieldSize,
      withValidation,
      fieldClassName = '',
      fieldMandatory,
      fieldWithAdditionalEl,
      withTooltip,
      tooltipAnchor,
      tooltipContent,
      tooltipOpenDelay,
      tooltipPosition,
      tooltipClassName = '',
      titleText,
      actionsBefore,
      actionsAfter,
      ref,
      inputProps
    } = this.props;
    
    const inputOmittedProps = [].concat(ABSTRACT_FIELD_OMITTED_PROPS, FIELD_OMITTED_PROPS, TOOLTIP_OMITTED_PROPS, 'actionsBefore', 'actionsAfter', 'inputProps');

    const inputPropsFinal = _.omit(this.props, inputOmittedProps);

    const asFieldProps = {
      fieldClassName,
      fieldMandatory,
      fieldWithAdditionalEl
    };

    const fieldCls = classNames(
      `tp-textbox-wrap tp-input-with-multiple-actions ${className} k-flex-1 k-d-flex-col`,
      {
        'k-state-empty': !value,
        'k-small': fieldSize === 'small',
        'k-large': fieldSize === 'large',
        'k-medium': fieldSize === 'medium',
        'k-state-invalid': valid === false,
        'k-state-disabled': disabled,
        'k-state-locked': locked
      }
    );

    const inputContent = (
      <Input
        id={this.props.id}
        ref={(input: any) => {
          this.inputReference = input;
          if (ref) {
            ref(input);
          }
        }}
        {...inputPropsFinal}
        data-cy={dataCy || 'puzzle-input'}
        title={titleText}
        onChange={(e) => {
          if (inputProps?.onChange) {
            inputProps.onChange(e);
          }
        }}
        onFocus={(e) => {
          if (inputProps?.onFocus) {
            inputProps.onFocus(e);
          }
          this.setState({
            isInputFocused: true
          });
        }}
        onBlur={(e) => {
          if (inputProps?.onBlur) {
            inputProps.onBlur(e);
          }
          this.setState({
            isInputFocused: false
          });
        }}
      />
    );

    const content = (
      <div
        className={fieldCls}
        data-cy={dataCy ? `${dataCy}-wrapper` : 'puzzle-input-wrapper'}
      >
        {label && <Label
          editorId={this.props.id}
          className={classNames({
            [labelClassName]: labelClassName,
            'k-mandatory': !asField && fieldMandatory
          })}
        >
          {label}
        </Label>}
        <HBox
          gap={1}
          alignItemsCenter
          className={`tp-textbox ${this.state.isInputFocused ? 'k-focus' : ''}`}
        >
          <HBox gap={1} className={'tp-actions-before'} alignItemsCenter>
            {this.actionsRender(actionsBefore)}
          </HBox>
          {withTooltip ?
            <WithTooltip
              className={tooltipClassName}
              context={this}
              title={titleText}
              htmlTag={'span'}
            >
              {inputContent}
            </WithTooltip> : inputContent
          }
          <HBox gap={1} className={'tp-actions-after'} alignItemsCenter>
            {this.actionsRender(actionsAfter)}
          </HBox>
        </HBox>
        {withValidation && <div
          data-cy={dataCy ? `${dataCy}-error-message` : 'error-message'}
          className={'tp-validation-error-message'}
          id={ariaDescribedBy}
        >
          {errorTxt}
        </div>}
        <PuzzleTooltip
          anchorElement={tooltipAnchor}
          ref={(el: any) => this.tooltip = el}
          openDelay={tooltipOpenDelay}
          position={tooltipPosition}
          content={tooltipContent}
        />
      </div>
    );

    if (asField) {
      return (
        <Field {...asFieldProps}>
          {content}
        </Field>
      );
    }

    return content;
  }
}

export default PuzzleInputWithActions;
